
import { technologyWiseData, calculateCountrySummaries } from "./TotalVolumes";
import { calculateResidualIntensities } from "./ResidualIntensities";
import { calculateRenewableIntensities } from "./RenewableIntensities";
import { calculateNonRenewableIntensities } from "./NonRenewableIntensities";
import { processDataByYear } from "./CommonUtils";
import { createExportableData } from "./ExportUtilities";

// Constants for error messages
const ERROR_MESSAGES = {
  PROCESSING_ERROR: 'Error processing data: ',
  FILE_FORMAT_ERROR: 'Failed to process Excel file. Please check the file format.',
  FILE_READ_ERROR: 'Error reading the file'
};

// Constants for sheet names
const SHEET_NAMES = {
  SHELL_GEN_POWER: 'Shell Gen Power',
  RESIDUAL_GRID_INTENSITIES: 'Residual Grid Intensities',
  TECH_INTENSITIES: 'Tech Intensities',
  TD_FACTORS: 'T & D Factors',
  RENEWABLE_CERTIFICATES: 'Renewable Certificates',
  GEN_TECH_SPECIFIC: '3P Gen Tech Specific',
  TOTAL_3P_GEN_SALES: 'Total 3P Gen Sales',
  SHELL_GENERATED_INTENSITIES: 'Shell Generated Intensities'
};

// Constants for workbook
const WORKBOOK_CONFIG = {
  SHEET_NAME: 'Carbon Data',
  TYPE: 'binary'
};

// Constants for timers
const TIMEOUT_DURATION = 1500;

/**
 * Process Excel file and extract data
 * @param {File} file - The Excel file to process
 * @param {Object} options - Configuration options
 * @param {Function} options.setUploadState - State setter for upload state
 * @param {Function} options.setErrorMessage - State setter for error message
 * @param {Function} options.setExcelData - State setter for raw Excel data
 * @param {Function} options.setProcessedResults - State setter for processed results
 * @param {Function} options.setIsFileUploadSuccess - State setter for upload success flag
 * @param {string} options.businessSegment - Selected business segment
 * @param {string} options.selectedYear - Selected year
 */
export function processExcelFile(
  file,
  {
    setUploadState,
    setErrorMessage,
    setExcelData,
    setProcessedResults,
    setIsFileUploadSuccess,
    businessSegment,
    selectedYear
  }
) {
  // Move XLSX access inside the function
  const XLSX = window.XLSX;
  const { read, utils } = XLSX;
  const reader = new FileReader();
  
  reader.onload = e => {
    try {
      const data = e.target.result;
      const workbook = read(data, { type: 'array' });
      
      // Create a JSON object with sheet names as keys
      const result = {};
      
      // Loop through each sheet in the workbook
      workbook.SheetNames.forEach(sheetName => {
        // Convert sheet to JSON with headers as keys
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = utils.sheet_to_json(worksheet);
        result[sheetName] = jsonData;
      });
      
      // Process the data to handle selected year actuals
      const selectedYearInt = parseInt(selectedYear, 10);
      const processedYearData = processDataByYear(result, selectedYearInt);
      
      // Process the data immediately with the result object, not the state
      try {
        const processedData = technologyWiseData(processedYearData);
        
        // Use the processed data directly
        const shellGenPowerData = processedYearData[SHEET_NAMES.SHELL_GEN_POWER] || [];
        const totalVolumes = calculateCountrySummaries(processedYearData, shellGenPowerData);
        
        const residualIntensities = calculateResidualIntensities(
          processedData, 
          processedYearData[SHEET_NAMES.RESIDUAL_GRID_INTENSITIES] || [],
        );

        
        const renewableIntensities = calculateRenewableIntensities(
          processedYearData[SHEET_NAMES.RENEWABLE_CERTIFICATES] || [], 
          processedYearData[SHEET_NAMES.TECH_INTENSITIES] || [], 
          processedYearData[SHEET_NAMES.GEN_TECH_SPECIFIC] || [],
          processedYearData[SHEET_NAMES.TOTAL_3P_GEN_SALES] || [],
          processedYearData[SHEET_NAMES.TD_FACTORS] || []
        );

        
        const nonRenewableIntensities = calculateNonRenewableIntensities(
          shellGenPowerData,
          processedYearData[SHEET_NAMES.GEN_TECH_SPECIFIC] || [],
          processedYearData[SHEET_NAMES.TECH_INTENSITIES] || [],
          processedYearData[SHEET_NAMES.SHELL_GENERATED_INTENSITIES] || [],
          processedYearData[SHEET_NAMES.TOTAL_3P_GEN_SALES] || [],
          processedYearData[SHEET_NAMES.TD_FACTORS] || []
        );
        
        const finalResult = {
          totalVolumes,
          residualIntensities,
          renewableIntensities,
          nonRenewableIntensities
        };
        // Generate exportable data with business segment and year
        const exportData = createExportableData(
          finalResult, 
          businessSegment, 
          selectedYearInt
        );
        
        // Store all the processed results
        setProcessedResults({
          finalResult,
          exportData
        });
        
      } catch (processingError) {
        setUploadState('error');
        setErrorMessage(`${ERROR_MESSAGES.PROCESSING_ERROR}${processingError.message}`);
      }
      
      // Store the raw excel data
      setExcelData(result);
      setUploadState('success');
      setIsFileUploadSuccess(true);
      setTimeout(() => setIsFileUploadSuccess(false), TIMEOUT_DURATION);
      
    } catch (error) {
      setUploadState('error');
      setErrorMessage(error.message || ERROR_MESSAGES.FILE_FORMAT_ERROR);
    }
  };
  
  reader.onerror = () => {
    setUploadState('error');
    setErrorMessage(ERROR_MESSAGES.FILE_READ_ERROR);
  };
  
  // Read the file as an ArrayBuffer
  reader.readAsArrayBuffer(file);
}

/**
 * Export data to Excel
 * @param {Array} data - Data array to export
 * @param {string} fileName - Filename for the exported file
 */
export function exportToExcel(data, fileName) {
  const XLSX = window.XLSX;
  const { utils, writeFile } = XLSX;
  // Create a new workbook
  const wb = utils.book_new();
  
  // Convert data to worksheet
  const ws = utils.json_to_sheet(data);
  
  // Add worksheet to workbook
  utils.book_append_sheet(wb, ws, WORKBOOK_CONFIG.SHEET_NAME);
  
  // Write workbook and trigger download
  writeFile(wb, fileName);
}

/**
 * Filter data based on search term
 * @param {Array} data - Data array to filter
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} - Filtered data array
 */
export function filterDataBySearchTerm(data, searchTerm) {
  if (!data || !searchTerm) {
    return data || [];
  }
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  
  return data.filter(item => {
    // Check if any field contains the search term
    return Object.keys(item).some(key => {
      // Make sure the value exists and can be converted to string
      const value = item[key];
      if (value === undefined || value === null) {
        return false;
      }
      
      // Convert to string and check if it includes the search term
      return String(value).toLowerCase().includes(lowerSearchTerm);
    });
  });
}

/**
 * Generate available years for selection
 * @param {number} currentYear - Current year
 * @param {number} numberOfPreviousYears - Number of previous years to include
 * @returns {Array} - Array of year strings
 */
export function generateAvailableYears(currentYear, numberOfPreviousYears = 3) {
  const years = [currentYear.toString()];
  
  for (let i = 1; i <= numberOfPreviousYears; i++) {
    years.push((currentYear - i).toString());
  }
  
  return years;
}

/**
 * Create business segment options
 * @returns {Array} - Array of business segment options
 */
export function getBusinessSegmentOptions() {
  return [
    { label: 'Shell Energy (SE)', value: 'SE' },
    { label: 'Shell Energy Trading (SET)', value: 'SET' }
  ];
}

/**
 * Generate table columns for data display
 * @returns {Array} - Array of column definitions
 */
export function getTableColumns() {
  return [
    {
      header: 'Region',
      accessorKey: 'Region',
    },
    {
      header: 'Country',
      accessorKey: 'Country',
    },
    {
      header: 'KPI',
      accessorKey: 'KPI',
    },
    {
      header: 'Delivery Year',
      accessorKey: 'Delivery Year',
    },
    {
      header: 'Value',
      accessorKey: 'Value',
    },
    {
      header: 'Unit of Measure',
      accessorKey: 'Unit of Measure',
    }
  ];
}